// Field editors for the review screen. Every editor is controlled: it gets a
// value slice and reports the whole slice back via onChange, so the page owns
// one AgpResume state object (which autosaves as revisions).

import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import {
  BULLET_MARKERS,
  LOW_CONFIDENCE_THRESHOLD,
  MAX_BULLET_LEVEL,
  lineLevel,
  lineMarker,
  normalizeLine,
  type BulletMarker,
  type CertificationEntry,
  type ContentLine,
  type EducationEntry,
  type EmploymentEntry,
  type ExtractedDate,
  type LanguageEntry,
  type OtherSection,
  type RoleEntry,
} from "@agp/shared";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import { Input, Label, Select, Textarea } from "@/components/ui/input";

// ---- shared helpers ---------------------------------------------------------

export function isLowConfidence(confidence: number): boolean {
  return confidence < LOW_CONFIDENCE_THRESHOLD;
}

export function SectionCard({
  title,
  lowConfidence,
  actions,
  children,
}: {
  title: string;
  lowConfidence?: boolean;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card className={cn(lowConfidence && "border-warn")}>
      <CardHeader>
        <div className="flex items-center gap-2">
          <CardTitle>{title}</CardTitle>
          {lowConfidence && <Badge variant="warn">Check this</Badge>}
        </div>
        {actions}
      </CardHeader>
      <CardBody className="flex flex-col gap-3">{children}</CardBody>
    </Card>
  );
}

function move<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item!);
  return next;
}

function ListItemControls({
  onMoveUp,
  onMoveDown,
  onRemove,
}: {
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex shrink-0 items-center gap-0.5">
      <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Move up" onClick={onMoveUp}>
        <ArrowUp size={14} />
      </Button>
      <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Move down" onClick={onMoveDown}>
        <ArrowDown size={14} />
      </Button>
      <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Remove" onClick={onRemove}>
        <Trash2 size={14} />
      </Button>
    </div>
  );
}

function AddButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button variant="secondary" size="sm" onClick={onClick} className="self-start">
      <Plus size={14} /> {label}
    </Button>
  );
}

// ---- primitive list editors -------------------------------------------------

export function StringListEditor({
  items,
  onChange,
  placeholder,
  multiline,
}: {
  items: string[];
  onChange: (items: string[]) => void;
  placeholder?: string;
  multiline?: boolean;
}) {
  const Field = multiline ? Textarea : Input;
  return (
    <div className="flex flex-col gap-2">
      {items.map((item, i) => (
        <div key={i} className="flex items-start gap-2">
          <Field
            value={item}
            rows={2}
            placeholder={placeholder}
            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
              const next = [...items];
              next[i] = e.target.value;
              onChange(next);
            }}
          />
          <ListItemControls
            onMoveUp={() => onChange(move(items, i, i - 1))}
            onMoveDown={() => onChange(move(items, i, i + 1))}
            onRemove={() => onChange(items.filter((_, j) => j !== i))}
          />
        </div>
      ))}
      <AddButton label="Add" onClick={() => onChange([...items, ""])} />
    </div>
  );
}

/** Content lines under a role or other-section: each row is a paragraph
 *  or a list line with its level (five deep) and marker style — arrow,
 *  number, dot… — matching how it renders in the output (AGP feedback:
 *  not everything is a bullet; nesting and marker styles are kept). */
export function ContentLinesEditor({
  items,
  onChange,
}: {
  items: ContentLine[];
  onChange: (items: ContentLine[]) => void;
}) {
  const set = (i: number, line: ContentLine) => {
    const next = [...items];
    next[i] = line;
    onChange(next);
  };
  return (
    <div className="flex flex-col gap-2">
      {items.map((raw, i) => {
        const line = normalizeLine(raw);
        return (
        <div key={i} className="flex items-start gap-2">
          <div className="flex w-[118px] shrink-0 flex-col gap-1">
            <Select
              value={line.kind === "para" ? "para" : "bullet"}
              aria-label="Line type"
              onChange={(e) =>
                set(i, e.target.value === "para" ? { text: line.text, kind: "para" } : { text: line.text, kind: "bullet", level: 0, marker: "dot" })
              }
            >
              <option value="bullet">List line</option>
              <option value="para">¶ Paragraph</option>
            </Select>
            {line.kind !== "para" && (
              <div className="flex gap-1">
                <Select
                  className="w-[52px] px-1"
                  value={lineLevel(line)}
                  aria-label="Level"
                  title="Nesting level"
                  onChange={(e) => set(i, { ...line, level: Number(e.target.value) })}
                >
                  {Array.from({ length: MAX_BULLET_LEVEL + 1 }, (_, l) => (
                    <option key={l} value={l}>
                      L{l + 1}
                    </option>
                  ))}
                </Select>
                <Select
                  className="w-[62px] px-1"
                  value={lineMarker(line)}
                  aria-label="Marker"
                  title="Marker style"
                  onChange={(e) => set(i, { ...line, marker: e.target.value as BulletMarker })}
                >
                  {BULLET_MARKERS.map((m) => (
                    <option key={m.key} value={m.key}>
                      {m.glyph}
                    </option>
                  ))}
                </Select>
              </div>
            )}
          </div>
          <Textarea
            value={line.text}
            rows={2}
            placeholder="Responsibility, achievement or paragraph"
            onChange={(e) => set(i, { ...line, text: e.target.value })}
          />
          <ListItemControls
            onMoveUp={() => onChange(move(items, i, i - 1))}
            onMoveDown={() => onChange(move(items, i, i + 1))}
            onRemove={() => onChange(items.filter((_, j) => j !== i))}
          />
        </div>
        );
      })}
      <AddButton label="Add line" onClick={() => onChange([...items, { text: "", kind: "bullet", level: 0, marker: "dot" }])} />
    </div>
  );
}

// ---- date editor ------------------------------------------------------------

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function emptyDate(): ExtractedDate {
  return { rawText: null, year: null, month: null, isPresent: false, confidence: 1 };
}

export function DateEditor({
  label,
  value,
  onChange,
  allowPresent,
}: {
  label: string;
  value: ExtractedDate | null;
  onChange: (d: ExtractedDate | null) => void;
  allowPresent?: boolean;
}) {
  const d = value ?? emptyDate();
  const set = (patch: Partial<ExtractedDate>) => {
    // A user edit overrides the extraction: full confidence, no raw text.
    onChange({ ...d, rawText: null, confidence: 1, ...patch });
  };
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex items-center gap-2">
        <Select
          className="w-[88px]"
          value={d.isPresent ? "" : (d.month ?? "")}
          disabled={allowPresent && d.isPresent}
          onChange={(e) =>
            set({ month: e.target.value === "" ? null : Number(e.target.value), isPresent: false })
          }
          aria-label={`${label} month`}
        >
          <option value="">—</option>
          {MONTH_NAMES.map((m, i) => (
            <option key={m} value={i + 1}>
              {m}
            </option>
          ))}
        </Select>
        <Input
          className="w-[84px]"
          type="number"
          placeholder="Year"
          value={d.isPresent ? "" : (d.year ?? "")}
          disabled={allowPresent && d.isPresent}
          onChange={(e) =>
            set({ year: e.target.value === "" ? null : Number(e.target.value), isPresent: false })
          }
          aria-label={`${label} year`}
        />
        {allowPresent && (
          <label className="flex items-center gap-1.5 text-[13px] text-text-2">
            <input
              type="checkbox"
              checked={d.isPresent}
              onChange={(e) =>
                set({ isPresent: e.target.checked, year: null, month: null })
              }
            />
            Present
          </label>
        )}
      </div>
      <p className="mt-1 text-[11px] text-text-3">
        Leave month as “—” for year-only dates — the output keeps that precision.
      </p>
    </div>
  );
}

// ---- languages / certifications --------------------------------------------

export function LanguagesEditor({
  items,
  onChange,
}: {
  items: LanguageEntry[];
  onChange: (items: LanguageEntry[]) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      {items.map((lang, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input
            value={lang.language}
            placeholder="Language"
            onChange={(e) => {
              const next = [...items];
              next[i] = { ...lang, language: e.target.value };
              onChange(next);
            }}
          />
          <Input
            value={lang.statedLevel ?? ""}
            placeholder="Level, exactly as stated"
            onChange={(e) => {
              const next = [...items];
              next[i] = { ...lang, statedLevel: e.target.value || null };
              onChange(next);
            }}
          />
          <ListItemControls
            onMoveUp={() => onChange(move(items, i, i - 1))}
            onMoveDown={() => onChange(move(items, i, i + 1))}
            onRemove={() => onChange(items.filter((_, j) => j !== i))}
          />
        </div>
      ))}
      <AddButton
        label="Add language"
        onClick={() => onChange([...items, { language: "", statedLevel: null, confidence: 1 }])}
      />
    </div>
  );
}

export function CertificationsEditor({
  items,
  onChange,
}: {
  items: CertificationEntry[];
  onChange: (items: CertificationEntry[]) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      {items.map((cert, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input
            value={cert.name ?? ""}
            placeholder="Certification"
            onChange={(e) => {
              const next = [...items];
              next[i] = { ...cert, name: e.target.value || null };
              onChange(next);
            }}
          />
          <Input
            value={cert.issuer ?? ""}
            placeholder="Issuer"
            className="max-w-[180px]"
            onChange={(e) => {
              const next = [...items];
              next[i] = { ...cert, issuer: e.target.value || null };
              onChange(next);
            }}
          />
          <Input
            type="number"
            value={cert.year ?? ""}
            placeholder="Year"
            className="w-[84px]"
            onChange={(e) => {
              const next = [...items];
              next[i] = { ...cert, year: e.target.value === "" ? null : Number(e.target.value) };
              onChange(next);
            }}
          />
          <ListItemControls
            onMoveUp={() => onChange(move(items, i, i - 1))}
            onMoveDown={() => onChange(move(items, i, i + 1))}
            onRemove={() => onChange(items.filter((_, j) => j !== i))}
          />
        </div>
      ))}
      <AddButton
        label="Add certification"
        onClick={() =>
          onChange([...items, { name: null, issuer: null, year: null, confidence: 1 }])
        }
      />
    </div>
  );
}

// ---- experience -------------------------------------------------------------

function emptyRole(): RoleEntry {
  return { title: null, start: null, end: null, bullets: [], confidence: 1 };
}

function emptyEmployer(): EmploymentEntry {
  return {
    employer: null,
    employerLocation: null,
    employerDescription: null,
    scale: null,
    start: null,
    end: null,
    roles: [emptyRole()],
    confidence: 1,
  };
}

function RoleEditor({
  role,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
  removable,
}: {
  role: RoleEntry;
  onChange: (r: RoleEntry) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  removable: boolean;
}) {
  return (
    <div
      className={cn(
        "rounded-md border border-border bg-surface-2/50 p-3",
        isLowConfidence(role.confidence) && "border-warn",
      )}
    >
      <div className="flex items-start gap-2">
        <div className="flex-1">
          <Label>Job title</Label>
          <Input
            value={role.title ?? ""}
            onChange={(e) => onChange({ ...role, title: e.target.value || null })}
          />
        </div>
        <div className="w-40">
          <Label>Location</Label>
          <Input
            value={role.location ?? ""}
            placeholder="If this role's own"
            onChange={(e) => onChange({ ...role, location: e.target.value || null })}
          />
        </div>
        {removable && (
          <div className="mt-5 flex shrink-0 items-center gap-0.5">
            <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Move role up" onClick={onMoveUp}>
              <ArrowUp size={14} />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Move role down" onClick={onMoveDown}>
              <ArrowDown size={14} />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Remove role" onClick={onRemove}>
              <Trash2 size={14} />
            </Button>
          </div>
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-4">
        <DateEditor label="From" value={role.start} onChange={(d) => onChange({ ...role, start: d })} />
        <DateEditor
          label="To"
          value={role.end}
          onChange={(d) => onChange({ ...role, end: d })}
          allowPresent
        />
      </div>
      <div className="mt-2">
        <Label>Content</Label>
        <ContentLinesEditor
          items={role.bullets}
          onChange={(bullets) => onChange({ ...role, bullets })}
        />
      </div>
    </div>
  );
}

export function ExperienceEditor({
  items,
  onChange,
}: {
  items: EmploymentEntry[];
  onChange: (items: EmploymentEntry[]) => void;
}) {
  const update = (i: number, entry: EmploymentEntry) => {
    const next = [...items];
    next[i] = entry;
    onChange(next);
  };
  return (
    <div className="flex flex-col gap-3">
      {items.map((entry, i) => (
        <div
          key={i}
          data-entry={`experience-${i}`}
          className={cn(
            "rounded-lg border border-border bg-surface p-4",
            isLowConfidence(entry.confidence) && "border-warn",
          )}
        >
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <h4 className="text-[15px]">{entry.employer || "New employer"}</h4>
              {isLowConfidence(entry.confidence) && <Badge variant="warn">Check this</Badge>}
            </div>
            <ListItemControls
              onMoveUp={() => onChange(move(items, i, i - 1))}
              onMoveDown={() => onChange(move(items, i, i + 1))}
              onRemove={() => onChange(items.filter((_, j) => j !== i))}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Employer</Label>
              <Input
                value={entry.employer ?? ""}
                onChange={(e) => update(i, { ...entry, employer: e.target.value || null })}
              />
            </div>
            <div>
              <Label>Location</Label>
              <Input
                value={entry.employerLocation ?? ""}
                onChange={(e) => update(i, { ...entry, employerLocation: e.target.value || null })}
              />
            </div>
          </div>
          <div className="mt-2 flex flex-wrap gap-4">
            <DateEditor
              label="Employer from"
              value={entry.start}
              onChange={(d) => update(i, { ...entry, start: d })}
            />
            <DateEditor
              label="Employer to"
              value={entry.end}
              onChange={(d) => update(i, { ...entry, end: d })}
              allowPresent
            />
          </div>
          <div className="mt-2">
            <Label>Company description (rendered as plain text, not bullets)</Label>
            <Textarea
              rows={2}
              value={entry.employerDescription ?? ""}
              placeholder="Listing status, headcount, business lines…"
              onChange={(e) =>
                update(i, { ...entry, employerDescription: e.target.value || null })
              }
            />
          </div>
          <div className="mt-2">
            <Label>規模 / team size (renders as “規模: …” for Japanese values, “Team: …” otherwise)</Label>
            <Input
              value={entry.scale ?? ""}
              placeholder="70名（パートナー会社含む）…"
              onChange={(e) => update(i, { ...entry, scale: e.target.value || null })}
            />
          </div>
          <div className="mt-3 flex flex-col gap-2">
            <Label>Roles at this employer (promotions stay grouped)</Label>
            {entry.roles.map((role, ri) => (
              <RoleEditor
                key={ri}
                role={role}
                removable={entry.roles.length > 1}
                onChange={(r) => {
                  const roles = [...entry.roles];
                  roles[ri] = r;
                  update(i, { ...entry, roles });
                }}
                onRemove={() =>
                  update(i, { ...entry, roles: entry.roles.filter((_, rj) => rj !== ri) })
                }
                onMoveUp={() => update(i, { ...entry, roles: move(entry.roles, ri, ri - 1) })}
                onMoveDown={() => update(i, { ...entry, roles: move(entry.roles, ri, ri + 1) })}
              />
            ))}
            <AddButton
              label="Add role"
              onClick={() => update(i, { ...entry, roles: [...entry.roles, emptyRole()] })}
            />
          </div>
        </div>
      ))}
      <AddButton label="Add employer" onClick={() => onChange([...items, emptyEmployer()])} />
    </div>
  );
}

// ---- education --------------------------------------------------------------

export function EducationEditor({
  items,
  onChange,
}: {
  items: EducationEntry[];
  onChange: (items: EducationEntry[]) => void;
}) {
  const update = (i: number, entry: EducationEntry) => {
    const next = [...items];
    next[i] = entry;
    onChange(next);
  };
  return (
    <div className="flex flex-col gap-3">
      {items.map((entry, i) => (
        <div
          key={i}
          data-entry={`education-${i}`}
          className={cn(
            "rounded-lg border border-border bg-surface p-4",
            isLowConfidence(entry.confidence) && "border-warn",
          )}
        >
          <div className="mb-2 flex items-center justify-end">
            <ListItemControls
              onMoveUp={() => onChange(move(items, i, i - 1))}
              onMoveDown={() => onChange(move(items, i, i + 1))}
              onRemove={() => onChange(items.filter((_, j) => j !== i))}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Institution</Label>
              <Input
                value={entry.institution ?? ""}
                onChange={(e) => update(i, { ...entry, institution: e.target.value || null })}
              />
            </div>
            <div>
              <Label>Location</Label>
              <Input
                value={entry.location ?? ""}
                onChange={(e) => update(i, { ...entry, location: e.target.value || null })}
              />
            </div>
            <div>
              <Label>Qualification</Label>
              <Input
                value={entry.qualification ?? ""}
                placeholder="B.A., MBA…"
                onChange={(e) => update(i, { ...entry, qualification: e.target.value || null })}
              />
            </div>
            <div>
              <Label>Field</Label>
              <Input
                value={entry.field ?? ""}
                onChange={(e) => update(i, { ...entry, field: e.target.value || null })}
              />
            </div>
            {(
              [
                ["Start", "startMonth", "startYear"],
                ["End", "endMonth", "endYear"],
              ] as const
            ).map(([label, monthKey, yearKey]) => (
              <div key={label}>
                <Label>{label}</Label>
                <div className="flex items-center gap-2">
                  <Select
                    className="w-[88px]"
                    value={entry[monthKey] ?? ""}
                    onChange={(e) =>
                      update(i, {
                        ...entry,
                        [monthKey]: e.target.value === "" ? null : Number(e.target.value),
                      })
                    }
                    aria-label={`${label} month`}
                  >
                    <option value="">—</option>
                    {MONTH_NAMES.map((m, mi) => (
                      <option key={m} value={mi + 1}>
                        {m}
                      </option>
                    ))}
                  </Select>
                  <Input
                    className="w-[84px]"
                    type="number"
                    placeholder="Year"
                    value={entry[yearKey] ?? ""}
                    onChange={(e) =>
                      update(i, {
                        ...entry,
                        [yearKey]: e.target.value === "" ? null : Number(e.target.value),
                      })
                    }
                    aria-label={`${label} year`}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-2">
            <Label>Honours</Label>
            <Input
              value={entry.honours ?? ""}
              onChange={(e) => update(i, { ...entry, honours: e.target.value || null })}
            />
          </div>
          <div className="mt-2">
            <Label>Bullets</Label>
            <StringListEditor
              items={entry.bullets}
              onChange={(bullets) => update(i, { ...entry, bullets })}
              multiline
            />
          </div>
        </div>
      ))}
      <AddButton
        label="Add education"
        onClick={() =>
          onChange([
            ...items,
            {
              institution: null,
              location: null,
              qualification: null,
              field: null,
              startYear: null,
              endYear: null,
              startMonth: null,
              endMonth: null,
              honours: null,
              bullets: [],
              confidence: 1,
            },
          ])
        }
      />
    </div>
  );
}

// ---- other sections ---------------------------------------------------------

export function OtherSectionsEditor({
  items,
  onChange,
}: {
  items: OtherSection[];
  onChange: (items: OtherSection[]) => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      {items.map((section, i) => (
        <div key={i} className="rounded-lg border border-border bg-surface p-4">
          <div className="flex items-center gap-2">
            <Input
              value={section.heading}
              placeholder="Section heading"
              onChange={(e) => {
                const next = [...items];
                next[i] = { ...section, heading: e.target.value };
                onChange(next);
              }}
            />
            <ListItemControls
              onMoveUp={() => onChange(move(items, i, i - 1))}
              onMoveDown={() => onChange(move(items, i, i + 1))}
              onRemove={() => onChange(items.filter((_, j) => j !== i))}
            />
          </div>
          <div className="mt-2">
            <ContentLinesEditor
              items={section.bullets}
              onChange={(bullets) => {
                const next = [...items];
                next[i] = { ...section, bullets };
                onChange(next);
              }}
            />
          </div>
        </div>
      ))}
      <AddButton
        label="Add section"
        onClick={() => onChange([...items, { heading: "", bullets: [], confidence: 1 }])}
      />
    </div>
  );
}
